<script setup lang="ts">
import { nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import BaseButton from '@/common/components/molecules/baseButton/BaseButton.vue';
import TagItem from '@/common/components/molecules/tag/TagItem.vue';

const MORE_COUNT_WIDTH = 40;
const GAP = 8;

interface Props {
  items?: string[];
  itemSize?: 'xsmall' | 'small' | 'medium';
}

const props = withDefaults(defineProps<Props>(), {
  items: () => [],
  itemSize: 'xsmall',
});

const wrapperRef = ref<HTMLElement | null>(null);
const visibleItems = ref<string[]>([]);
const moreCount = ref<number>(0);
const expanded = ref<boolean>(false);

const calcVisible = async () => {
  if (expanded.value) {
    visibleItems.value = [...props.items];
    moreCount.value = 0;
    return;
  }

  visibleItems.value = [];
  await nextTick();

  const wrapperWidth = wrapperRef.value?.getBoundingClientRect().width ?? 0;

  if (!wrapperWidth || !props.items.length) {
    visibleItems.value = props.items;
    moreCount.value = 0;
    return;
  }

  // 모든 아이템을 임시로 렌더링하여 실제 너비 측정
  visibleItems.value = [...props.items];
  await nextTick();

  const children =
    wrapperRef.value?.querySelectorAll<HTMLElement>('.collapsible-tag-list__item') ?? [];

  if (!children.length) {
    visibleItems.value = props.items;
    moreCount.value = 0;
    return;
  }

  let count = 0;
  let usedWidth = 0;
  const SAFETY_MARGIN = 4; // 브라우저 렌더링 차이를 위한 여유 공간

  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    const childWidth = child.getBoundingClientRect().width;

    // 남은 아이템이 있으면 +n 버튼 너비 고려
    const remainingCount = props.items.length - (i + 1);
    const moreButtonWidth = remainingCount > 0 ? MORE_COUNT_WIDTH : 0;

    if (count === 0) {
      // 첫 번째 아이템의 너비 확인
      const firstItemWithMore = childWidth + moreButtonWidth + SAFETY_MARGIN;
      if (firstItemWithMore <= wrapperWidth) {
        usedWidth += childWidth;
        count += 1;
      } else {
        // 첫 번째 아이템이 너무 길면 최소한 하나는 표시
        usedWidth += childWidth;
        count += 1;
        // 첫 번째 아이템만으로도 공간을 초과하면 더 이상 추가하지 않음
        break;
      }
    } else {
      const widthWithGap = usedWidth + GAP + childWidth;
      const totalWidthWithMore = widthWithGap + moreButtonWidth + SAFETY_MARGIN;

      if (totalWidthWithMore <= wrapperWidth) {
        usedWidth = widthWithGap;
        count += 1;
      } else {
        // 더 이상 추가할 수 없으면 중단
        break;
      }
    }
  }

  // 최소 한 개는 노출되도록 보정
  if (count === 0) count = 1;

  visibleItems.value = props.items.slice(0, count);
  moreCount.value = props.items.length - count;
};

const onClickMore = () => {
  expanded.value = true;
  visibleItems.value = [...props.items];
  moreCount.value = 0;
};

const ro = new ResizeObserver(() => {
  calcVisible();
});

onMounted(() => {
  calcVisible();
  if (wrapperRef.value) {
    ro.observe(wrapperRef.value);
  }
});

onUnmounted(() => {
  ro.disconnect();
});

watch(
  () => props.items,
  () => {
    expanded.value = false;
    calcVisible();
  },
  { deep: true },
);
</script>

<template>
  <div
    ref="wrapperRef"
    :class="['collapsible-tag-list', { 'collapsible-tag-list--expanded': expanded }]"
  >
    <tag-item
      v-for="(item, index) in visibleItems"
      :key="index"
      :size="props.itemSize"
      class="collapsible-tag-list__item"
      :title="item"
    >
      {{ item }}
    </tag-item>

    <base-button
      v-if="moreCount > 0 && !expanded"
      class="collapsible-tag-list__more"
      type="button"
      tag="button"
      @click="onClickMore"
    >
      {{ `+${moreCount}` }}
    </base-button>
  </div>
</template>

<style lang="scss" scoped>
.collapsible-tag-list {
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
  align-items: center;
  width: 100%;
  min-width: 0;
  overflow: hidden;

  &__item {
    flex-shrink: 0;
  }

  &__more {
    height: 16px;
    padding: 0 8px;
    cursor: pointer;
    user-select: none !important;
    border-radius: 29px;
    background-color: var(--badge-bg-color);
    font-size: 11px;
    line-height: 16px;
    color: var(--main-font-color);
  }
}
:deep(.tag-item) {
  max-width: none;
}
:deep(.tag-item__container),
:deep(.tag-item__container > span) {
  overflow: visible;
  text-overflow: initial;
  white-space: nowrap;
}
.collapsible-tag-list--expanded {
  flex-wrap: wrap;
  align-items: flex-start;
  overflow: visible;
}
</style>
